import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Select } from '@components';
import { PurchaseNumberRequest } from '../types';
import { useCountryCodes } from '../hooks/useCountryCodes';

interface BuyNumberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchase: (data: PurchaseNumberRequest) => Promise<void>;
}

export const BuyNumberModal: React.FC<BuyNumberModalProps> = ({ isOpen, onClose, onPurchase }) => {
  const { t } = useTranslation();
  const { countryCodes, loading: countryCodesLoading } = useCountryCodes();
  const [formData, setFormData] = useState<PurchaseNumberRequest>({
    country_code: '',
  });
  const [errors, setErrors] = useState<Partial<PurchaseNumberRequest>>({});
  const [loading, setLoading] = useState(false);

  const countryOptions = countryCodes.map(country => ({
    value: country.code,
    label: `${country.title} (${country.code_number})`,
  }));

  const handleInputChange = (field: keyof PurchaseNumberRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<PurchaseNumberRequest> = {};

    if (!formData.country_code.trim()) {
      newErrors.country_code = t('phoneNumbers.countryCodeRequiredError');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onPurchase(formData);
      setFormData({ country_code: '' });
      setErrors({});
    } catch (error) {
      console.error('Purchase failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ country_code: '' });
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('phoneNumbers.buyPhoneNumber')} size="md">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div>
            <label
              htmlFor="country_code"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('phoneNumbers.countryRequired')}
            </label>
            <Select
              id="country_code"
              value={formData.country_code}
              onChange={value =>
                handleInputChange('country_code', Array.isArray(value) ? value[0] : value)
              }
              options={countryOptions}
              placeholder={t('phoneNumbers.selectCountry')}
              error={!!errors.country_code}
              errorText={errors.country_code}
              disabled={countryCodesLoading || loading}
            />
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="material-icons text-blue-600 dark:text-blue-400 text-lg">info</span>
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">{t('phoneNumbers.importantInformation')}</p>
              <ul className="space-y-1 text-xs">
                <li>• {t('phoneNumbers.phoneNumbersPurchasedImmediately')}</li>
                <li>• {t('phoneNumbers.assignToAiAgents')}</li>
                <li>• {t('phoneNumbers.numbersCanBeDeleted')}</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
            className="flex-1"
          >
            {t('phoneNumbers.cancel')}
          </Button>
          <Button type="submit" variant="gradient-multi" loading={loading} className="flex-1">
            <span className="material-icons text-sm mr-2">phone</span>
            {t('phoneNumbers.purchaseNumber')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
